import { brokerService } from 'api/services/broker'
import {
  ExtractDropdownItem,
  GetStatementListResponseBody,
  GetStatementResponseBody,
  StatementListItem,
} from 'api/types/broker'
import { Wrapper } from 'components'
import { AuthContext } from 'contexts/AuthContext'
import {
  Button,
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { useContext, useEffect, useState } from 'react'

export const Extract = () => {
  const { currentBroker } = useContext(AuthContext)
  const [dropdownItems, setDropdownItems] = useState<ExtractDropdownItem[]>([])
  const [extracts, setExtracts] = useState<StatementListItem[]>([])
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null)

  const getStatementList = async () => {
    if (!currentBroker || !currentBroker.BrokerNo) return
    try {
      const response = (await brokerService.getBrokerStatementList({
        brokerNo: currentBroker.BrokerNo,
        yearMonth: selectedMonth || '',
      })) as GetStatementListResponseBody
      setExtracts(response.Statements)
    } catch (error) {
      console.error('Error fetching statement list:', error)
      toast.error('Erro ao obter extratos. Por favor, tente novamente.')
    }
  }

  const handleDownload = async (statementId: string, docType: string) => {
    if (!currentBroker || !currentBroker.BrokerNo) return
    try {
      const response = (await brokerService.getBrokerStatement({
        brokerNo: currentBroker.BrokerNo,
        statementId: statementId,
      })) as GetStatementResponseBody
      const type =
        docType.trim() === '.pdf'
          ? 'application/pdf'
          : 'aapplication/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const byteCharacters = atob(response.Statement.File)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: type })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `extrato_${statementId}${docType.trim()}`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading statement:', error)
      toast.error('Erro ao descarregar o extrato. Por favor, tente novamente.')
    }
  }

  const getLastSixMonths = () => {
    const locale = 'pt-PT'
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long' })

    const result: ExtractDropdownItem[] = []
    const now = new Date()

    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const year = d.getFullYear()
      const monthNum = d.getMonth() + 1
      const value = parseInt(`${year}${String(monthNum).padStart(2, '0')}`)

      const monthName = monthFormatter.format(d)
      const label = `${year} ${
        monthName.charAt(0).toUpperCase() + monthName.slice(1)
      }`

      result.push({ value, label })
    }

    setDropdownItems(result)
  }

  useEffect(() => {
    if (selectedMonth) {
      getStatementList()
    }
  }, [selectedMonth])

  useEffect(() => {
    getLastSixMonths()
  }, [])

  return (
    <div className="w-full flex-1 py-12">
      <Wrapper>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Peça aqui o seu extrato
        </h2>
        <h1
          className={`pb-10 text-xxxl font-medium ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
        >
          Extrato de conta
        </h1>

        <div className="lg:py-8">
          <h3 className="text-metal-900 uppercase font-bold text-body-4 pb-2">
            Selecione o mês
          </h3>
          <p className="text-metal-300 text-body-5 pb-2">(Últimos 6 meses)</p>
          <div className="flex items-center gap-1 max-w-sm">
            <div>
              <Select
                value={selectedMonth || ''}
                onValueChange={e => {
                  setSelectedMonth(e)
                }}
              >
                <SelectAction className="whitespace-nowrap gap-4">
                  <SelectValue className="pr-8" placeholder="Ano/Mês" />
                </SelectAction>
                <SelectContent>
                  {dropdownItems.map(item => (
                    <SelectItem
                      className="pr-8"
                      key={item.value}
                      value={String(item.value)}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {extracts.length > 0 ? (
          <div className="mt-12">
            <h2 className="text-metal-900 uppercase font-bold text-body-4 pb-2">
              Extratos solicitados
            </h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extracts.map((extract, index) => (
                  <TableRow key={index}>
                    <TableCell>{extract.StatementMonth}</TableCell>
                    <TableCell>{extract.StatementName}</TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        color="primary"
                        onClick={() =>
                          handleDownload(extract.StatementID, extract.FileType)
                        }
                      >
                        Descarregar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <></>
        )}
      </Wrapper>
    </div>
  )
}
